import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { apiHandler } from '../../api/apihandler';
import { ALLERGY_ACTION_PLANS_LIST, ALLERGY_ACTION_PLAN_SUBMIT } from '../../api/constants';

// Fetch allergy action plans list
export const fetchAllergyActionPlans = createAsyncThunk(
  'allergyActionPlan/fetchAllergyActionPlans',
  async ({ page = 1, limit = 10 }, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append('page', page);
      formData.append('limit', limit);
      
      const response = await apiHandler(ALLERGY_ACTION_PLANS_LIST, 'POST', formData);
      return response.data.data.data; // response.data.data.data contains { meta, data }
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch allergy action plans');
    }
  }
);

// Submit allergy action plan (create/edit)
export const submitAllergyActionPlan = createAsyncThunk(
  'allergyActionPlan/submitAllergyActionPlan',
  async (planData, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      Object.keys(planData).forEach(key => {
        if (planData[key] !== null && planData[key] !== undefined) {
          formData.append(key, planData[key]);
        }
      });
      
      const response = await apiHandler(ALLERGY_ACTION_PLAN_SUBMIT, 'POST', formData);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to submit allergy action plan');
    }
  }
);

const allergyActionPlanSlice = createSlice({
  name: 'allergyActionPlan',
  initialState: {
    plans: [],
    meta: null,
    loading: false,
    error: null,
    submitLoading: false,
    submitError: null
  },
  reducers: {
    clearErrors: (state) => {
      state.error = null;
      state.submitError = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch allergy action plans
      .addCase(fetchAllergyActionPlans.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllergyActionPlans.fulfilled, (state, action) => {
        state.loading = false;
        state.plans = action.payload.data;
        state.meta = action.payload.meta;
      })
      .addCase(fetchAllergyActionPlans.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Submit allergy action plan
      .addCase(submitAllergyActionPlan.pending, (state) => {
        state.submitLoading = true;
        state.submitError = null;
      })
      .addCase(submitAllergyActionPlan.fulfilled, (state, action) => {
        state.submitLoading = false;
        // Add or update plan in the list
        const planIndex = state.plans.findIndex(p => p.id === action.payload.data.id);
        if (planIndex !== -1) {
          state.plans[planIndex] = action.payload.data;
        } else {
          state.plans.unshift(action.payload.data);
        }
      })
      .addCase(submitAllergyActionPlan.rejected, (state, action) => {
        state.submitLoading = false;
        state.submitError = action.payload;
      });
  }
});

export const { clearErrors } = allergyActionPlanSlice.actions;

// Selectors
export const selectPlans = (state) => state.allergyActionPlan.plans;
export const selectMeta = (state) => state.allergyActionPlan.meta;
export const selectLoading = (state) => state.allergyActionPlan.loading;
export const selectError = (state) => state.allergyActionPlan.error;
export const selectSubmitLoading = (state) => state.allergyActionPlan.submitLoading;
export const selectSubmitError = (state) => state.allergyActionPlan.submitError;
export const selectPlanById = (state, planId) => 
  state.allergyActionPlan.plans.find(plan => plan.id === parseInt(planId));

export default allergyActionPlanSlice.reducer;
